import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { buildVehicleDistribution } from './statisticsData';
import { getKontrastFarbe } from '../einteilung/constants';

const percent = value => `${Number(value || 0).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;

function SegmentLabel({ cx, cy, midAngle, outerRadius, percent: share, payload }) {
  if (share < 0.05) return null;
  const angle = -midAngle * Math.PI / 180;
  const radius = outerRadius * 0.66;
  return <text x={cx + radius * Math.cos(angle)} y={cy + radius * Math.sin(angle)}
    fill={getKontrastFarbe(payload.color)} textAnchor="middle" dominantBaseline="central"
    fontSize={11} fontWeight={600} pointerEvents="none">{payload.label}</text>;
}

export default function PersonalStatistics({ statistik, fahrzeuge = [] }) {
  const stats = statistik || {};
  const distribution = buildVehicleDistribution(stats, fahrzeuge);
  const vehicles = (stats.fahrzeuge || []).filter(vehicle => vehicle.anzahl_haupt || vehicle.anzahl_springer);
  const metrics = [
    ['Hauptdienste gesamt', stats.anzahl_gesamt],
    ['Tagdienste', stats.anzahl_tag],
    ['Nachtdienste', stats.anzahl_nacht],
    ['Springer-Einteilungen', stats.anzahl_springer_total],
  ];
  return (
    <div className="space-y-5" data-testid="personal-statistics">
      {!statistik && <p className="text-muted-foreground">Für dich sind in diesem Zeitraum noch keine Einteilungen erfasst.</p>}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {metrics.map(([label, value]) => (
          <Card key={label}><CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="mt-2 text-3xl font-semibold tabular-nums">{value || 0}</p>
          </CardContent></Card>
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Deine Dienstverteilung</CardTitle>
            <CardDescription>Hauptdienste nach Funktion, in den eingestellten Fahrzeugfarben. Springer-Einteilungen werden separat gezählt.</CardDescription>
          </CardHeader>
          <CardContent>
            {distribution.length > 0 ? <>
              <div className="h-64 w-full" role="img" aria-label="Tortendiagramm der Hauptdienste nach Fahrzeug; Zahlen und Anteile stehen in der folgenden Liste.">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={distribution} dataKey="value" nameKey="name" outerRadius="85%" isAnimationActive={false} label={SegmentLabel} labelLine={false}>
                      {distribution.map(part => <Cell key={part.id} fill={part.color} />)}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} Hauptdienste`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-2 mt-3">
                {distribution.map(part => <li key={part.id} className="flex items-center gap-2 text-sm">
                  <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: part.color }} />
                  <span className="flex-1">{part.name}</span>
                  <span className="font-medium tabular-nums">{part.value} · {percent(part.percent)}</span>
                </li>)}
              </ul>
            </> : <p className="py-16 text-center text-muted-foreground">Noch keine Hauptdienste für das Tortendiagramm vorhanden.</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Deine Zahlen im Überblick</CardTitle>
            <CardDescription>Dieselbe Zählweise wie in „Statistik gesamt“.</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="divide-y">
              {[
                ['Rettungsdienst', stats.anzahl_rd, stats.prozent_rd],
                ['Löschfahrzeug', stats.anzahl_lhf, stats.prozent_lhf],
                ['Sonstige Fahrzeuge', stats.anzahl_sonstige, stats.prozent_sonstige],
                ['Wandern (WA)', stats.anzahl_wandern, stats.prozent_wandern],
                ['RTW-Wandern (RW)', stats.anzahl_rtw_wandern, stats.prozent_rtw_wandern],
                ['ZBV', stats.anzahl_zbv, stats.prozent_zbv],
              ].map(([label, value, share]) => <div key={label} className="flex items-center justify-between gap-3 py-3">
                <dt className="text-sm">{label}</dt>
                <dd className="font-medium tabular-nums">{value || 0} <span className="text-sm font-normal text-muted-foreground">· {percent(share)}</span></dd>
              </div>)}
            </dl>
            <p className="mt-4 text-xs text-muted-foreground">WA, RW und ZBV werden zusätzlich zu den Hauptdiensten ausgewiesen. Ihre Prozentwerte entsprechen den Bezugsgrößen der Gesamttabelle.</p>
          </CardContent>
        </Card>
      </div>
      {vehicles.length > 0 && <Card>
        <CardHeader><CardTitle>Fahrzeuge und Funktionen</CardTitle>
          <CardDescription>Deine Haupt- und Springer-Einteilungen im Detail.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {vehicles.map(vehicle => <div key={vehicle.fahrzeug_id} className="rounded-lg border p-4">
            <h3 className="font-semibold">{vehicle.funkrufname || vehicle.kurzname}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{vehicle.anzahl_haupt || 0} Hauptdienste · {vehicle.anzahl_springer || 0} Springer</p>
            <p className="text-xs text-muted-foreground">Hauptdienste: {vehicle.haupt_tag || 0} Tag / {vehicle.haupt_nacht || 0} Nacht</p>
            <ul className="mt-3 space-y-1 text-sm">
              {(vehicle.funktionen || []).filter(fn => fn.anzahl_gesamt > 0).map(fn => <li key={fn.kuerzel} className="flex justify-between gap-2">
                <span>{fn.name || fn.kuerzel} ({fn.kuerzel})</span><span className="font-medium">{fn.anzahl_gesamt}</span>
              </li>)}
            </ul>
          </div>)}
        </CardContent>
      </Card>}
    </div>
  );
}
